import { useState, useEffect, useRef, useMemo, ReactNode, Dispatch, SetStateAction } from 'react';
import { ParsedData, Loan, LoanEvent } from '@/types/nsldsTypes';
import { LoansContext } from '@/context/loans-context-definition';
import { UserSurveyData } from '@/components/survey/user-survey';

import { useFinancialHardship } from '@/hooks/useFinancialHardship';
// import { useTopServicer } from '@/hooks/useTopServicer';
import { AllPlansResult } from '@/types/loanCalculationTypes';
import {
  AllPlanSummaries,
  // DetailedPlanSummary, // Not directly used here, but AllPlanSummaries implies it
  PlanSummaryData,
  // EligibilityStatus, // Removed import, will rely on inference for getScopedEligibility
} from '@/types/planSummaryTypes';
import { getEligiblePlans as _getEligiblePlans } from '@/utils/paymentPlans/getEligiblePlans';
import { CALCULATION_ASSUMPTIONS as _CALCULATION_ASSUMPTIONS } from '@/utils/constants';
import { PlanType, BorrowerType } from '@/types/planTypes';
import { calculateLoanScenario } from '@/utils/paymentPlans/scenarioCalculator';

interface LoansProviderProps {
  children: ReactNode;
  userParsedData: ParsedData | null;
  partnerParsedData: ParsedData | null;
  surveyData: UserSurveyData | null;
  userFile: File | null;
  partnerFile: File | null;
  setUserFile: Dispatch<SetStateAction<File | null>>;
  setPartnerFile: Dispatch<SetStateAction<File | null>>;
  userManualLoan: Loan | null;
  partnerManualLoan: Loan | null;
  isMobile?: boolean;
  handleGoBackToDataInput?: () => void;
  hasAttemptedCalculation: boolean;
}

export function LoansProvider({
  children,
  userParsedData,
  partnerParsedData,
  surveyData,
  userFile,
  partnerFile,
  setUserFile,
  setPartnerFile,
  userManualLoan,
  partnerManualLoan,
  isMobile = false,
  handleGoBackToDataInput,
  hasAttemptedCalculation,
}: LoansProviderProps) {
  const [showOnlyInRepayment, setShowOnlyInRepayment] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null);
  const [loansByStatus, setLoansByStatus] = useState<Record<string, Loan[]>>({});
  const [totalsByStatus, setTotalsByStatus] = useState<Record<string, number>>({});
  const [filteredLoans, setFilteredLoans] = useState<Loan[]>([]);
  const [totalCombinedLoansCount, setTotalCombinedLoansCount] = useState(0);
  const [selectedLoanEvents, setSelectedLoanEvents] = useState<LoanEvent[]>([]);
  const [accordionValue, setAccordionValue] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const initialSelectionDone = useRef(false);
  const [refiInterestRate, setRefiInterestRate] = useState(0.07);
  const [refiPeriodYears, setRefiPeriodYears] = useState(30);
  const [taxRate, setTaxRate] = useState(0.3);
  const [inflationRate, setInflationRate] = useState(0.03);
  const [incomeGrowthRate, setIncomeGrowthRate] = useState(0.03);
  const [scenarioResults, setScenarioResults] = useState<AllPlansResult[] | null>(null);
  const [scenarioPlanSummaries, setScenarioPlanSummaries] = useState<AllPlanSummaries | null>(null);
  const [scenarioCalculationError, setScenarioCalculationError] = useState<string | null>(null);
  const [scenarioCalculationLoading, setScenarioCalculationLoading] = useState(false);

  // Memoized calculation for combinedLoans
  // Prefixes loan IDs with borrower type to ensure uniqueness when combining
  const combinedLoans = useMemo(() => {
    const allLoans: Loan[] = [];

    // Add user loans with prefixed IDs
    if (userParsedData && userParsedData.loansData.length > 0) {
      userParsedData.loansData.forEach((loan) => {
        allLoans.push({
          ...loan,
          Borrower: 'user',
          'Loan Award ID': `user_${loan['Loan Award ID']}`,
        });
      });
    } else if (userManualLoan) {
      allLoans.push(userManualLoan); // Already has Borrower: 'user' and unique ID
    }

    const shouldIncludePartnerData = surveyData?.includePartner ?? false;

    // Add partner loans with prefixed IDs
    if (shouldIncludePartnerData) {
      if (partnerParsedData && partnerParsedData.loansData.length > 0) {
        partnerParsedData.loansData.forEach((loan) => {
          allLoans.push({
            ...loan,
            Borrower: 'partner',
            'Loan Award ID': `partner_${loan['Loan Award ID']}`,
          });
        });
      } else if (partnerManualLoan) {
        allLoans.push(partnerManualLoan); // Already has Borrower: 'partner' and unique ID
      }
    }

    allLoans.forEach((loan) => {
      loan.qualifiedPaymentMonths = Math.max(
        loan.qualifiedPaymentMonths || 0,
        loan['Loan PSLF Cumulative Matched Months'] || 0
      );
    });
    return allLoans;
  }, [userParsedData, partnerParsedData, userManualLoan, partnerManualLoan, surveyData]);

  // Effect 1: Process data and calculate groups/totals, now using memoized combinedLoans
  useEffect(() => {
    setTotalCombinedLoansCount(combinedLoans.length);

    if (combinedLoans.length === 0) {
      setFilteredLoans([]);
      setLoansByStatus({});
      setTotalsByStatus({});
      setSelectedLoan(null);
      setAccordionValue(null);
      initialSelectionDone.current = false;
      return;
    }

    // Filter loans based on repayment status
    const currentFilteredLoans = showOnlyInRepayment
      ? combinedLoans.filter((loan) => loan.isInRepayment)
      : combinedLoans;
    setFilteredLoans(currentFilteredLoans);

    // Group loans by status
    const currentLoansByStatus: Record<string, Loan[]> = {};
    currentFilteredLoans.forEach((loan) => {
      const status = loan['Current Loan Status Description'] || 'Unknown status';
      currentLoansByStatus[status] = [];

      currentLoansByStatus[status].push(loan);
    });
    setLoansByStatus(currentLoansByStatus);

    // Calculate total by status
    const currentTotalsByStatus: Record<string, number> = {};
    Object.entries(currentLoansByStatus).forEach(([status, loans]) => {
      currentTotalsByStatus[status] = loans.reduce((sum, loan) => {
        return sum + Number(loan['Total Outstanding'] || 0);
      }, 0);
    });
    setTotalsByStatus(currentTotalsByStatus);

    // Reset the selection flag when data/filter changes
    initialSelectionDone.current = false;

    // Set the first accordion item to be open by default
    if (Object.keys(currentLoansByStatus).length > 0) {
      setAccordionValue(Object.keys(currentLoansByStatus)[0]);
    }
  }, [showOnlyInRepayment, combinedLoans]);

  // Combined effect for handling loan selection
  useEffect(() => {
    // Function to auto-select first loan if needed
    function handleAutoSelection() {
      const hasLoans = Object.values(loansByStatus).flat().length > 0;

      // Auto-select the first loan if we have loans and haven't done initial selection yet
      if (hasLoans && !initialSelectionDone.current) {
        const statuses = Object.keys(loansByStatus);
        if (statuses.length > 0 && loansByStatus[statuses[0]].length > 0) {
          setSelectedLoan(loansByStatus[statuses[0]][0]);
          initialSelectionDone.current = true;
        }
      } else if (!hasLoans) {
        // Reset selection if there are no loans
        setSelectedLoan(null);
        initialSelectionDone.current = false;
      }
    }

    // Run the selection logic
    handleAutoSelection();
  }, [loansByStatus]);

  // Update events when selected loan changes
  useEffect(() => {
    if (!selectedLoan) {
      setSelectedLoanEvents([]);
      return;
    }

    // Determine which dataset (user or partner) the selected loan belongs to
    const relevantParsedData =
      selectedLoan.Borrower === 'user' ? userParsedData : partnerParsedData;

    // Fetch events only if the loan is from a parsed file (not manual)
    if (relevantParsedData) {
      const { loanEventsData } = relevantParsedData;
      const events = loanEventsData.filter(
        (event) => event['Loan Award ID'] === selectedLoan['Loan Award ID']
      );
      setSelectedLoanEvents(events);
    } else {
      setSelectedLoanEvents([]);
    }
  }, [userParsedData, partnerParsedData, selectedLoan]);

  // --- Call Custom Hooks ---
  const meetsFinancialHardship = useFinancialHardship(combinedLoans, surveyData);
  // const topServicer = useTopServicer(userParsedData?.loansData, partnerParsedData?.loansData);

  // --- Calculate Weighted Average Interest Rate ---
  const weightedAverageInterestRate = useMemo(() => {
    if (combinedLoans.length === 0) {
      return 0;
    }

    const totalBalance = combinedLoans.reduce((acc, loan) => acc + loan['Total Outstanding'], 0);
    if (totalBalance === 0) {
      return 0; // Avoid division by zero
    }

    const weightedSum = combinedLoans.reduce(
      (acc, loan) => acc + loan['Total Outstanding'] * loan['Loan Interest Rate'],
      0
    );

    return weightedSum / totalBalance;
  }, [combinedLoans]);
  // --- End Calculation ---

  // Scenario calculation effect
  useEffect(
    () => {
      if (!hasAttemptedCalculation) {
        setScenarioResults(null);
        setScenarioPlanSummaries(null);
        setScenarioCalculationError(null);
        setScenarioCalculationLoading(false);
        return;
      }

      const actualFamilySize = surveyData?.familySize ?? 1;
      const actualIncludePartner = surveyData?.includePartner ?? false;
      const actualFilingStatus = actualIncludePartner
        ? surveyData?.filingStatus ?? 'MFJ'
        : 'Single';

      // console.log('[LoansContext] Starting scenario calculation effect', {
      //   hasSurveyData: !!surveyData,
      //   filteredLoansCount: filteredLoans.length,
      //   meetsFinancialHardship,
      //   taxRate,
      //   inflationRate,
      //   actualFamilySize,
      //   actualIncludePartner,
      //   actualFilingStatus,
      // });

      if (
        !surveyData ||
        surveyData.annualIncome === null ||
        surveyData.state === null ||
        (actualIncludePartner && surveyData.filingStatus === null) ||
        (actualIncludePartner && surveyData.partnerAnnualIncome === null)
      ) {
        console.log(
          '[LoansContext] Essential survey data missing. User income, state, and (if partner included) partner income and filing status are required.'
        );
        if (scenarioResults || scenarioPlanSummaries || scenarioCalculationError) {
          setScenarioResults(null);
          setScenarioPlanSummaries(null);
          setScenarioCalculationError(null);
        }
        setScenarioCalculationLoading(false);
        return;
      }

      if (filteredLoans.length === 0) {
        setScenarioResults(null);
        setScenarioPlanSummaries(null);
        setScenarioCalculationError(null);
        setScenarioCalculationLoading(false);
        return;
      }

      setScenarioCalculationLoading(true);
      setScenarioCalculationError(null);

      try {
        // 1a. Consolidate Borrower Data (borrowersDataForCalc)
        const borrowersDataForCalc: import('@/types/loanCalculationTypes').BorrowerData[] = [];
        borrowersDataForCalc.push({
          borrowerIdentifier: 'Me',
          state: surveyData.state,
          familySize: actualFilingStatus === 'MFS' ? actualFamilySize - 1 : actualFamilySize,
          futureAnnualGrossIncome: [surveyData.annualIncome],
          pslfEligible: surveyData.pslfEligible,
        });

        if (actualIncludePartner) {
          borrowersDataForCalc.push({
            borrowerIdentifier: 'My partner',
            state: surveyData.state,
            familySize: 1,
            futureAnnualGrossIncome: [surveyData.partnerAnnualIncome as number],
            pslfEligible: surveyData.pslfEligiblePartner,
          });
        }

        // 1b. Prepare Household Data (householdDataForCalc)
        const householdDataForCalc: import('@/types/loanCalculationTypes').HouseholdData = {
          filingStatus: actualFilingStatus,
        };

        // 1c. Pre-calculate Eligibility for all loans
        // const eligibilityByLoanId = new Map<string, PlanCriterionResult[]>();
        // filteredLoans.forEach((loan) => {
        //   const eligibilityResults = _getEligiblePlans({
        //     loan,
        //     allLoans: filteredLoans, // Pass all loans for context like "new borrower" status
        //     income: surveyData.annualIncome || 0, // TODO: This should be borrower-specific income
        //     state: surveyData.state || 'CA', // TODO: This should be borrower-specific state
        //     pslfEligible:
        //       loan.Borrower === 'user'
        //         ? surveyData.pslfEligible || false
        //         : surveyData.pslfEligiblePartner || false,
        //     meetsFinancialHardship,
        //   });
        //   eligibilityByLoanId.set(
        //     loan['Loan Award ID'],
        //     Array.isArray(eligibilityResults) ? eligibilityResults : []
        //   );
        // });

        // 1d. Iterate by PlanType and Calculate Scenarios
        // Stores: Map<LoanAwardID, Map<PlanType, LoanResults>>
        const resultsByLoanThenPlan = new Map<
          string,
          Map<PlanType, import('@/types/loanCalculationTypes').LoanResults>
        >();

        Object.values(PlanType).forEach((currentPlanType) => {
          // Construct loansDataForCurrentPlanScenario for THIS plan type
          const loansDataForCurrentPlanScenario: import('@/types/loanCalculationTypes').LoanDataForScenario[] =
            filteredLoans.map((loan) => ({
              loanInput: loan,
              planType: currentPlanType, // Assign current plan type to all loans for this scenario run
              startingPrincipal: Number(loan['Outstanding Principal']),
              startingNonCapInterest: Number(loan['Outstanding Interest']),
            }));

          if (loansDataForCurrentPlanScenario.length === 0) {
            console.warn(
              `[LoansContext] No loans to process for plan ${currentPlanType}. Skipping.`
            );
            return;
          }

          const scenarioConfig: import('@/types/loanCalculationTypes').ScenarioConfig =
            currentPlanType === PlanType.PRIVATE_REFINANCE
              ? {
                  simulationStartDate: new Date().toISOString(),
                  inflationRate,
                  incomeGrowthRate,
                  taxRate,
                  refiInterestRate,
                  refiPeriodYears,
                }
              : {
                  simulationStartDate: new Date().toISOString(),
                  inflationRate,
                  incomeGrowthRate,
                  taxRate,
                  refiInterestRate: 0,
                  refiPeriodYears: 0,
                };

          try {
            // console.log(
            //   `[LoansContext] Calculating scenario for ALL loans under plan: ${currentPlanType}`,
            //   {
            //     numLoans: loansDataForCurrentPlanScenario.length,
            //     borrowers: borrowersDataForCalc.map((b) => b.borrowerIdentifier),
            //     filingStatus: householdDataForCalc.filingStatus,
            //   }
            // );

            const scenarioRunResults: import('@/types/loanCalculationTypes').LoanResults[] =
              calculateLoanScenario(
                loansDataForCurrentPlanScenario,
                borrowersDataForCalc,
                householdDataForCalc,
                scenarioConfig
              );

            // Store results: scenarioRunResults is an array of LoanResults, one for each loan in loansDataForCurrentPlanScenario
            scenarioRunResults.forEach((loanResult, index) => {
              const loanAwardId = loansDataForCurrentPlanScenario[index].loanInput['Loan Award ID'];
              if (!resultsByLoanThenPlan.has(loanAwardId)) {
                resultsByLoanThenPlan.set(
                  loanAwardId,
                  new Map<PlanType, import('@/types/loanCalculationTypes').LoanResults>()
                );
              }
              // The get below is guaranteed to return a Map because of the check above
              const mapForLoan = resultsByLoanThenPlan.get(loanAwardId);
              if (mapForLoan) {
                // Added check to satisfy linter for explicit undefined check
                mapForLoan.set(currentPlanType, loanResult);
              }
            });
          } catch (planError) {
            console.error(
              `[LoansContext] Error calculating scenario for plan ${currentPlanType}:`,
              planError
            );
            // If a plan calculation fails, we should still try others.
            // We can mark results for this plan as errored for all loans.
            filteredLoans.forEach((loan) => {
              const loanAwardId = loan['Loan Award ID'];
              if (!resultsByLoanThenPlan.has(loanAwardId)) {
                resultsByLoanThenPlan.set(
                  loanAwardId,
                  new Map<PlanType, import('@/types/loanCalculationTypes').LoanResults>()
                );
              }
              const mapForLoanOnError = resultsByLoanThenPlan.get(loanAwardId);
              if (mapForLoanOnError) {
                // Added check
                mapForLoanOnError.set(currentPlanType, {
                  planName: currentPlanType,
                  loanPaidOff: false,
                  payOffDate: null,
                  forgivenessOccurred: false,
                  forgivenessDate: null,
                  finalMonthIndex: 0,
                  finalPrincipal: Number(loan['Total Outstanding']),
                  totalLoanPaymentsRaw: 0,
                  totalLoanPaymentsDiscounted: 0,
                  totalForgiven: 0,
                  taxesOwedOnForgivenRaw: 0,
                  firstMonthPayment: 0,
                  maxMonthPayment: 0,
                  monthlyDetails: [],
                } as import('@/types/loanCalculationTypes').LoanResults);
              }
            });
          }
        });

        // 1e. Transform Stored Results into AllPlansResult[]
        const finalAllPlansResults: AllPlansResult[] = filteredLoans.map((loan) => {
          const loanAwardId = loan['Loan Award ID'];
          const loanResultsForThisLoan =
            resultsByLoanThenPlan.get(loanAwardId) ||
            new Map<PlanType, import('@/types/loanCalculationTypes').LoanResults>();

          const resultsArray: import('@/types/loanCalculationTypes').LoanResults[] = [];
          Object.values(PlanType).forEach((pt) => {
            const res = loanResultsForThisLoan.get(pt);
            if (res) {
              resultsArray.push(res);
            } else {
              // Push placeholder results if a specific plan calculation failed or was missing
              resultsArray.push({
                planName: pt,
                loanPaidOff: false,
                payOffDate: null,
                forgivenessOccurred: false,
                forgivenessDate: null,
                finalMonthIndex: 0,
                finalPrincipal: Number(loan['Total Outstanding']),
                totalLoanPaymentsRaw: 0,
                totalLoanPaymentsDiscounted: 0,
                totalForgiven: 0,
                taxesOwedOnForgivenRaw: 0,
                firstMonthPayment: 0,
                maxMonthPayment: 0,
                monthlyDetails: [],
              } as import('@/types/loanCalculationTypes').LoanResults);
            }
          });

          return {
            loanAwardId,
            loanDescription: loan['Loan Type Description'] || 'Unknown Loan Type',
            borrower: loan.Borrower as BorrowerType,
            results: resultsArray,
            // eligibilityCriteria: eligibilityByLoanId.get(loanAwardId) || [],
          };
        });
        setScenarioResults(finalAllPlansResults);

        // 1f. Calculate scenarioPlanSummaries
        const detailedPlanSummaries: AllPlanSummaries = Object.values(
          PlanType
        ).reduce<AllPlanSummaries>((acc, planType) => {
          const userLoansForPlanResults: AllPlansResult[] = [];
          const partnerLoansForPlanResults: AllPlansResult[] = [];

          finalAllPlansResults.forEach((loanPlanDetail) => {
            if (loanPlanDetail.borrower === 'user') {
              userLoansForPlanResults.push(loanPlanDetail);
            } else {
              partnerLoansForPlanResults.push(loanPlanDetail);
            }
          });

          // Helper to get loan calculation results for a specific plan type from a list of AllPlansResult
          function getLoanCalcResultsForPlan(
            resultsList: AllPlansResult[],
            pt: PlanType
          ): import('@/types/loanCalculationTypes').LoanResults[] {
            return resultsList
              .map((lpr) => lpr.results.find((r) => r.planName === pt))
              .filter(Boolean) as import('@/types/loanCalculationTypes').LoanResults[];
          }

          // Definition of aggregateResults - RE-ADDED
          function aggregateResults(
            results: import('@/types/loanCalculationTypes').LoanResults[]
          ): PlanSummaryData | null {
            if (results.length === 0) return null;

            const monthlyPayments = results
              .map((r) => r.firstMonthPayment)
              .filter((p): p is number => typeof p === 'number' && isFinite(p));
            const totalPaymentsRaw = results.map((r) => r.totalLoanPaymentsRaw).filter(isFinite);
            const totalPaymentsDiscounted = results
              .map((r) => r.totalLoanPaymentsDiscounted)
              .filter(isFinite);
            const totalForgivenAmounts = results.map((r) => r.totalForgiven).filter(isFinite);
            const taxesOwed = results.map((r) => r.taxesOwedOnForgivenRaw).filter(isFinite);
            const finishDates = results
              .map((r) => (r.loanPaidOff ? r.payOffDate : r.forgivenessDate))
              .filter((d): d is Date => d instanceof Date);

            return {
              firstMonthlyPayment:
                monthlyPayments.length > 0 ? monthlyPayments.reduce((a, b) => a + b, 0) : null,
              maxMonthlyPayment:
                results.length > 0 && results.every((r) => typeof r.maxMonthPayment === 'number')
                  ? results.map((r) => r.maxMonthPayment).reduce((a, b) => a + b, 0)
                  : null,
              totalPayment:
                totalPaymentsRaw.length > 0 ? totalPaymentsRaw.reduce((a, b) => a + b, 0) : null,
              taxesOwedOnForgivenRaw: taxesOwed.reduce((a, b) => a + b, 0) || 0,
              finishDate:
                finishDates.length > 0
                  ? new Date(Math.max(...finishDates.map((d) => d.getTime())))
                  : null,
              totalForgiven:
                totalForgivenAmounts.length > 0
                  ? totalForgivenAmounts.reduce((a, b) => a + b, 0)
                  : null,
              totalLoanPaymentsDiscounted:
                totalPaymentsDiscounted.length > 0
                  ? totalPaymentsDiscounted.reduce((a, b) => a + b, 0)
                  : null,
            };
          }
          // END RE-ADD of aggregateResults

          const userLoanCalcResults = getLoanCalcResultsForPlan(userLoansForPlanResults, planType);
          const partnerLoanCalcResults = getLoanCalcResultsForPlan(
            partnerLoansForPlanResults,
            planType
          );
          const combinedLoanCalcResults = getLoanCalcResultsForPlan(finalAllPlansResults, planType);

          const userSummary = aggregateResults(userLoanCalcResults);
          const partnerSummary = aggregateResults(partnerLoanCalcResults);
          const combinedSummary = aggregateResults(combinedLoanCalcResults);

          // Inlined getScopedEligibility calls
          acc[planType] = {
            user: userSummary,
            partner: partnerSummary,
            combined: combinedSummary || {
              firstMonthlyPayment: null,
              maxMonthlyPayment: null,
              totalPayment: null,
              taxesOwedOnForgivenRaw: 0,
              finishDate: null,
              totalForgiven: null,
              totalLoanPaymentsDiscounted: null,
            },
            // userEligibility:
            //   userLoansForPlanResults.length > 0
            //     ? getScopedEligibility(planType, userLoansForPlanResults)
            //     : null,
            // partnerEligibility:
            //   partnerLoansForPlanResults.length > 0
            //     ? getScopedEligibility(planType, partnerLoansForPlanResults)
            //     : null,
            // combinedEligibility: getScopedEligibility(planType, finalAllPlansResults),
          };
          return acc;
        }, {} as AllPlanSummaries);

        setScenarioPlanSummaries(detailedPlanSummaries);
        setScenarioCalculationLoading(false);
      } catch (err) {
        console.error('[LoansContext] Scenario calculation error (Revised):', err);
        setScenarioCalculationError(
          err instanceof Error ? err.message : 'An unexpected error occurred during calculations.'
        );
        setScenarioResults(null);
        setScenarioPlanSummaries(null);
        setScenarioCalculationLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      surveyData,
      filteredLoans,
      userManualLoan,
      partnerManualLoan,
      meetsFinancialHardship,
      taxRate,
      refiInterestRate,
      refiPeriodYears,
      inflationRate,
      incomeGrowthRate,
      hasAttemptedCalculation,
      // combinedLoans // Not needed as filteredLoans is derived and sufficient
    ]
  );

  // Helper function to get overall eligibility status for a plan type
  // Adjusted to accept AllPlansResult[] instead of LoanPlanDetails[]
  // Now also accepts a specific subset of AllPlansResult[] for scoped eligibility
  // function getScopedEligibility(planType: PlanType, scopedLoanResults: AllPlansResult[]) {
  //   if (scopedLoanResults.length === 0) {
  //     return { status: 'unknown', text: 'No loans in scope', color: 'gray' } as const;
  //   }
  //
  //   const isEligibleForAllScopedLoans = scopedLoanResults.every((loanResult) => {
  //     const criteriaForPlan = loanResult.eligibilityCriteria.filter((c) => c.plan === planType);
  //     return criteriaForPlan.length > 0 && criteriaForPlan.every((c) => c.isMet);
  //   });
  //
  //   if (isEligibleForAllScopedLoans) {
  //     return { status: 'all', text: 'Eligible', color: 'green' } as const;
  //   }
  //
  //   const isEligibleForAnyScopedLoan = scopedLoanResults.some((loanResult) => {
  //     const criteriaForPlan = loanResult.eligibilityCriteria.filter((c) => c.plan === planType);
  //     return criteriaForPlan.length > 0 && criteriaForPlan.every((c) => c.isMet);
  //   });
  //
  //   if (isEligibleForAnyScopedLoan) {
  //     return { status: 'some', text: 'Partially eligible', color: 'orange' } as const;
  //   }
  //
  //   return { status: 'none', text: 'Ineligible', color: 'red' } as const;
  // }

  // --- End Custom Hooks ---
  // Memoize the context value
  const value = useMemo(
    () => ({
      parsedData: null,
      userParsedData,
      partnerParsedData,
      surveyData,
      userFile,
      partnerFile,
      setUserFile,
      setPartnerFile,
      userManualLoan,
      partnerManualLoan,
      showOnlyInRepayment,
      setShowOnlyInRepayment,
      selectedLoan,
      setSelectedLoan,
      // topServicer,
      meetsFinancialHardship,
      loansByStatus,
      totalsByStatus,
      filteredLoans,
      totalCombinedLoansCount,
      selectedLoanEvents,
      accordionValue,
      setAccordionValue,
      modalOpen,
      setModalOpen,
      isMobile,
      inflationRate,
      setInflationRate,
      incomeGrowthRate,
      setIncomeGrowthRate,
      taxRate,
      setTaxRate,
      handleGoBackToDataInput,
      weightedAverageInterestRate,
      scenarioResults,
      scenarioPlanSummaries,
      scenarioCalculationError,
      scenarioCalculationLoading,
      refiInterestRate,
      setRefiInterestRate,
      refiPeriodYears,
      setRefiPeriodYears,
    }),
    [
      userParsedData,
      partnerParsedData,
      surveyData,
      userFile,
      partnerFile,
      setUserFile,
      setPartnerFile,
      userManualLoan,
      partnerManualLoan,
      showOnlyInRepayment,
      setShowOnlyInRepayment,
      selectedLoan,
      setSelectedLoan,
      // topServicer,
      meetsFinancialHardship,
      loansByStatus,
      totalsByStatus,
      filteredLoans,
      totalCombinedLoansCount,
      selectedLoanEvents,
      accordionValue,
      setAccordionValue,
      modalOpen,
      setModalOpen,
      isMobile,
      inflationRate,
      setInflationRate,
      incomeGrowthRate,
      setIncomeGrowthRate,
      taxRate,
      setTaxRate,
      handleGoBackToDataInput,
      weightedAverageInterestRate,
      scenarioResults,
      scenarioPlanSummaries,
      scenarioCalculationError,
      scenarioCalculationLoading,
      refiInterestRate,
      setRefiInterestRate,
      refiPeriodYears,
      setRefiPeriodYears,
    ]
  );

  return <LoansContext value={value}>{children}</LoansContext>;
}

// Remove the commented out section below
// // Export the hook
// // export function useLoans(): LoansContextValue { // Comment out or move this function
// //   const context = use(LoansContext);
// //   if (!context) {
// //     throw new Error('useLoans must be used within a LoansProvider');
// //   }
// //   return context;
// // }
//
// // To fix the react-refresh/only-export-components warning,
// // we can move useLoans to its own file, e.g., src/hooks/useLoans.ts
// // For now, let's comment it out if it's causing issues with the immediate check.
// // If it's actively used, moving it is the better long-term solution.
